// A real production build MUST have REACT_APP_ENV injected by the build pipeline (see
// Dockerfile ENV REACT_APP_ENV=${ENVIRONMENT}). If it's missing in a genuine production
// build, silently defaulting to "LOCAL" would flip the whole bundle into the legacy
// insecure JWT-in-cookie auth mode (see isReactLocalLikeEnv below) with no build-time
// error. Fail loudly instead — but only when NODE_ENV is actually 'production'; CRA's own
// dev server / test runner set NODE_ENV to 'development'/'test' and legitimately may not
// have REACT_APP_ENV set.
if (!process.env.REACT_APP_ENV && process.env.NODE_ENV === "production") {
  throw new Error(
    "REACT_APP_ENV is not set in a production build. Refusing to silently fall back to " +
      "LOCAL (insecure auth mode) — set REACT_APP_ENV at build time."
  );
}

export const REACT_APP_ENV = (process.env.REACT_APP_ENV || "LOCAL").trim();

const normalizedEnv = REACT_APP_ENV.toLowerCase();

type ReactEnvSuffix = "LOCAL" | "DEV" | "STAGE" | "PROD";
type ReactEnvVariants = Partial<Record<"default" | ReactEnvSuffix, string>>;

const reactEnvValues: Record<string, ReactEnvVariants> = {
  REACT_APP_ENV: {
    default: process.env.REACT_APP_ENV,
  },
  REACT_APP_ORCHESTRATION_API_URL: {
    default: process.env.REACT_APP_ORCHESTRATION_API_URL,
    LOCAL: process.env.REACT_APP_ORCHESTRATION_API_URL_LOCAL,
    DEV: process.env.REACT_APP_ORCHESTRATION_API_URL_DEV,
    STAGE: process.env.REACT_APP_ORCHESTRATION_API_URL_STAGE,
    PROD: process.env.REACT_APP_ORCHESTRATION_API_URL_PROD,
  },
  REACT_APP_ORCHESTRATION_WS_URL: {
    default: process.env.REACT_APP_ORCHESTRATION_WS_URL,
    LOCAL: process.env.REACT_APP_ORCHESTRATION_WS_URL_LOCAL,
    DEV: process.env.REACT_APP_ORCHESTRATION_WS_URL_DEV,
    STAGE: process.env.REACT_APP_ORCHESTRATION_WS_URL_STAGE,
    PROD: process.env.REACT_APP_ORCHESTRATION_WS_URL_PROD,
  },
  REACT_APP_USE_SERVER_SESSION_AUTH: {
    default: process.env.REACT_APP_USE_SERVER_SESSION_AUTH,
    LOCAL: process.env.REACT_APP_USE_SERVER_SESSION_AUTH_LOCAL,
    DEV: process.env.REACT_APP_USE_SERVER_SESSION_AUTH_DEV,
    STAGE: process.env.REACT_APP_USE_SERVER_SESSION_AUTH_STAGE,
    PROD: process.env.REACT_APP_USE_SERVER_SESSION_AUTH_PROD,
  },
  REACT_APP_API_URL: {
    default: process.env.REACT_APP_API_URL,
    LOCAL: process.env.REACT_APP_API_URL_LOCAL,
    DEV: process.env.REACT_APP_API_URL_DEV,
    STAGE: process.env.REACT_APP_API_URL_STAGE,
    PROD: process.env.REACT_APP_API_URL_PROD,
  },
  REACT_APP_SEND_PATIENT_NAME: {
    default: process.env.REACT_APP_SEND_PATIENT_NAME,
    LOCAL: process.env.REACT_APP_SEND_PATIENT_NAME_LOCAL,
    DEV: process.env.REACT_APP_SEND_PATIENT_NAME_DEV,
    STAGE: process.env.REACT_APP_SEND_PATIENT_NAME_STAGE,
    PROD: process.env.REACT_APP_SEND_PATIENT_NAME_PROD,
  },
  REACT_APP_ENABLE_DOCEREE_ADS: {
    default: process.env.REACT_APP_ENABLE_DOCEREE_ADS,
    LOCAL: process.env.REACT_APP_ENABLE_DOCEREE_ADS_LOCAL,
    DEV: process.env.REACT_APP_ENABLE_DOCEREE_ADS_DEV,
    STAGE: process.env.REACT_APP_ENABLE_DOCEREE_ADS_STAGE,
    PROD: process.env.REACT_APP_ENABLE_DOCEREE_ADS_PROD,
  },
  REACT_APP_DOCEREE_APPROVED_PHI_DISCLOSURE: {
    default: process.env.REACT_APP_DOCEREE_APPROVED_PHI_DISCLOSURE,
    LOCAL: process.env.REACT_APP_DOCEREE_APPROVED_PHI_DISCLOSURE_LOCAL,
    DEV: process.env.REACT_APP_DOCEREE_APPROVED_PHI_DISCLOSURE_DEV,
    STAGE: process.env.REACT_APP_DOCEREE_APPROVED_PHI_DISCLOSURE_STAGE,
    PROD: process.env.REACT_APP_DOCEREE_APPROVED_PHI_DISCLOSURE_PROD,
  },
};

function reactEnvSuffix(): string {
  if (["dev", "development"].includes(normalizedEnv)) {
    return "DEV";
  }
  if (["stage", "staging"].includes(normalizedEnv)) {
    return "STAGE";
  }
  if (["prod", "production"].includes(normalizedEnv)) {
    return "PROD";
  }
  return REACT_APP_ENV.toUpperCase();
}

export function getReactEnvValue(key: string, fallback = ""): string {
  const suffix = reactEnvSuffix();
  const values = reactEnvValues[key];
  return (
    (values?.[suffix as ReactEnvSuffix] || "").trim() ||
    (values?.default || "").trim() ||
    fallback
  );
}

export function getReactBooleanEnv(key: string, fallback = false): boolean {
  const value = getReactEnvValue(key, fallback ? "true" : "false").toLowerCase();
  return ["true", "1", "yes"].includes(value);
}

export function isReactStrictEnv(): boolean {
  return ["dev", "development", "stage", "staging", "prod", "production"].includes(
    normalizedEnv
  );
}

// A hostname-based sanity check: in a browser context, "local-like" behavior should only
// ever apply when the page is actually being served from localhost. This prevents a bad
// REACT_APP_ENV value alone (e.g. a misconfigured build, or the env var being dropped)
// from flipping production behavior into the insecure local/test auth mode — the env
// string and the hostname must agree. Outside a browser (SSR/build-time/tests) there's no
// hostname to check, so the env string alone is trusted there.
function isLocalHostname(): boolean {
  return (
    typeof window !== "undefined" &&
    ["localhost", "127.0.0.1"].includes(window.location.hostname)
  );
}

export function isReactLocalLikeEnv(): boolean {
  const explicitlyLocalLike = normalizedEnv === "local" || normalizedEnv === "test";
  if (typeof window !== "undefined") {
    // In-browser: require the hostname to actually agree with the env string, whether
    // the env string is explicitly local/test OR simply missing.
    return (
      (explicitlyLocalLike || !normalizedEnv) && isLocalHostname()
    );
  }
  // No browser context to corroborate against (SSR/build-time/tests) — fall back to the
  // env string alone.
  return explicitlyLocalLike || !normalizedEnv;
}

export function isDocereeAdsEnabled(): boolean {
  const enabled = getReactBooleanEnv("REACT_APP_ENABLE_DOCEREE_ADS");
  const approved = getReactBooleanEnv("REACT_APP_DOCEREE_APPROVED_PHI_DISCLOSURE");
  return enabled && (!isReactStrictEnv() || approved);
}
